import NavBar from '../components/layout/NavBar';
import Footer from '../components/layout/Footer';
import { Mail, MapPin, Phone, Clock } from 'lucide-react';

export const Contact = () => {
  const contactInfo = [
    {
      icon: Mail,
      title: "Email",
      details: ["hello@texstyleco.com", "support@texstyleco.com"],
    },
    {
      icon: Phone,
      title: "Phone",
      details: ["+974 - 44314668", "+974 - 77976999"],
    },
    {
      icon: MapPin,
      title: "Address",
      details: ["123 Kitchenware Lane", "San Francisco, CA 94102"],
    },
    {
      icon: Clock,
      title: "Hours",
      details: ["Mon - Fri: 9am - 6pm PST", "Sat: 10am - 4pm PST"],
    },
  ];

  const inquiryTypes = [
    {
      title: "Product Inquiries",
      description: "Questions about materials, care instructions, or which product suits your needs.",
      email: "products@texstyleco.com",
    },
    {
      title: "Order Support",
      description: "Tracking, returns, exchanges, or any questions about your purchase.",
      email: "support@texstyleco.com",
    },
    {
      title: "Partnerships",
      description: "Wholesale, collaborations, or press inquiries.",
      email: "partners@texstyleco.com",
    },
  ];

  return (
    <div data-testid="contact-page" className="min-h-screen bg-tex-bg">
      {/* Grain Overlay */}
      <div className="grain-overlay" />
      
      <NavBar />
      
      <main className="pt-20">
        {/* Hero Section */}
        <section className="relative min-h-[70vh] flex items-center">
          <div className="absolute inset-0 bg-gradient-radial from-tex-violet-deep/30 via-transparent to-transparent" />
          <div className="absolute inset-0 dot-matrix opacity-15" />
          
          <div className="relative z-10 max-w-5xl mx-auto px-6 md:px-12 lg:px-24 w-full">
            <p
              className="font-raleway text-[10px] font-semibold uppercase tracking-[0.5em] text-tex-violet-bright mb-6"
            >
              Get in Touch
            </p>
            
            <h1
              data-testid="contact-heading"
              className="text-editorial-hero text-white"
            >
              We'd love to hear
              <br />
              from <span className="text-tex-violet-bright">you.</span>
            </h1>
            
            <p
              className="font-raleway text-base md:text-lg font-light text-tex-muted max-w-2xl leading-relaxed mt-8"
            >
              Whether you have questions about our products, need assistance with an order, 
              or simply want to share your cooking story—we're here.
            </p>
          </div>
        </section>

        {/* Contact Information Grid */}
        <section className="py-24 md:py-32">
          <div className="max-w-6xl mx-auto px-6 md:px-12 lg:px-24">
            <div
              data-testid="contact-info-grid"
              className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6"
            >
              {contactInfo.map((info, index) => (
                <div
                  key={info.title}
                  data-testid={`contact-info-${index + 1}`}
                  className="bg-tex-surface/20 border border-white/5 p-8 hover:border-tex-violet/30 transition-all duration-500 group"
                >
                  <div className="w-12 h-12 rounded-full bg-tex-violet/10 flex items-center justify-center mb-6 group-hover:bg-tex-violet/20 transition-colors duration-300">
                    <info.icon className="w-5 h-5 text-tex-violet-bright" />
                  </div>
                  <h3 className="font-raleway text-[11px] font-semibold uppercase tracking-[0.3em] text-tex-warm-white mb-4">
                    {info.title}
                  </h3>
                  <div className="space-y-1">
                    {info.details.map((detail, i) => (
                      <p key={i} className="font-raleway text-sm font-light text-tex-muted">
                        {detail}
                      </p>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Inquiry Types */}
        <section className="py-24 md:py-32 relative">
          <div className="absolute inset-0 dot-matrix opacity-10" />
          
          <div className="relative z-10 max-w-5xl mx-auto px-6 md:px-12 lg:px-24">
            <p className="font-raleway text-[10px] font-semibold uppercase tracking-[0.5em] text-tex-violet-bright mb-6">
              Reach Out
            </p>
            <h2 
              data-testid="inquiry-heading"
              className="text-editorial-section text-white text-left mb-16"
            >
              How can we help?
            </h2>

            <div data-testid="inquiry-types" className="space-y-12">
              {inquiryTypes.map((inquiry, index) => (
                <div
                  key={inquiry.title}
                  data-testid={`inquiry-type-${index + 1}`}
                  className="flex flex-col md:flex-row md:items-center gap-6 md:gap-12 border-b border-white/5 pb-12"
                >
                  <div className="flex-1">
                    <h3 className="font-cormorant italic text-2xl md:text-3xl text-white mb-3">
                      {inquiry.title}
                    </h3>
                    <p className="font-raleway text-sm font-light text-tex-muted leading-relaxed">
                      {inquiry.description}
                    </p>
                  </div>
                  <a
                    href={`mailto:${inquiry.email}`}
                    className="font-mono text-xs text-tex-violet-bright hover:text-tex-warm-white transition-colors duration-300"
                  >
                    {inquiry.email} →
                  </a>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-24 md:py-32 relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-radial from-tex-violet-deep/20 via-transparent to-transparent" />
          
          <div className="relative z-10 max-w-4xl mx-auto px-6 md:px-12 lg:px-24">
            <h2 
              data-testid="faq-heading"
              className="text-editorial-section text-white text-left mb-12"
            >
              Common Questions
            </h2>
            
            <div className="space-y-8">
              {[
                {
                  q: "What is your return policy?",
                  a: "We offer a 30-day return policy for unused items in original packaging. Defective items are covered under our 10-year warranty.",
                },
                {
                  q: "Do you ship internationally?",
                  a: "Yes, we ship to most countries worldwide. Shipping costs and delivery times vary by location.",
                },
                {
                  q: "How do I care for my Tex-Style Company cookware?",
                  a: "Our stainless steel cookware is dishwasher safe. For best results, hand wash with mild soap and dry immediately.",
                },
              ].map((faq, index) => (
                <div
                  key={index}
                  data-testid={`faq-${index + 1}`}
                  className="border-b border-white/10 pb-8"
                >
                  <h3 className="font-raleway text-base font-medium text-tex-warm-white mb-3">
                    {faq.q}
                  </h3>
                  <p className="font-raleway text-sm font-light text-tex-muted leading-relaxed">
                    {faq.a}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default Contact;
